import { cn } from "@/lib/utils";
import { useBranding } from "@/contexts/BrandingContext";

// Intentional product wordmark used as the fallback when no image logo is set.
//   NIRVANA   (dominant, brand/heading font)
//   ContentOS (secondary, clean UI font, restrained gold)
function TextLockup({ shortName, productLabel, onDark, scale = "md", className }) {
  const sizes = {
    sm: { a: "text-base", b: "text-[10px]" },
    md: { a: "text-lg", b: "text-[11px]" },
    lg: { a: "text-2xl", b: "text-sm" },
  }[scale];
  return (
    <span className={cn("flex flex-col leading-none", className)} data-testid="brand-text-lockup">
      <span className={cn("font-heading font-bold tracking-tight", sizes.a, onDark ? "text-sidebar-fg" : "text-foreground")}>
        {(shortName || "Nirvana").toUpperCase()}
      </span>
      <span className={cn("mt-0.5 font-ui font-medium text-accent", sizes.b)}>
        {productLabel || "ContentOS"}
      </span>
    </span>
  );
}

// variant: "header" (desktop/expanded) | "mobile" | "login" | (compact prop for the icon rail)
export function Logo({ compact = false, onDark = false, className, variant = "header", size = "header" }) {
  const b = useBranding();
  const assets = b?.assets || {};
  const settings = b?.settings || {};
  const shortName = (b?.short_name || "Nirvana").trim() || "Nirvana";
  const productLabel = settings.product_label || "ContentOS";

  const maxH = size === "mobile" ? (settings.mobile_height || 28)
    : typeof size === "number" ? size : (settings.header_height || 36);
  const imgStyle = { maxHeight: `${maxH}px`, width: "auto" };
  const Img = ({ src, alt }) => <img src={src} alt={alt} style={imgStyle} className="object-contain" />;

  const fullSrc = onDark ? (assets.dark?.url || assets.full?.url) : (assets.light?.url || assets.full?.url);
  const markSrc = assets.mark?.url || null;

  // ---- Compact / collapsed icon rail: mark, else single-letter text (extreme constraint) ----
  if (compact) {
    if (markSrc) return <span className={cn("flex items-center", className)} data-testid="nirvana-logo"><Img src={markSrc} alt={shortName} /></span>;
    return (
      <span className={cn("flex items-center", className)} data-testid="nirvana-logo">
        <span className={cn("flex h-9 w-9 items-center justify-center rounded-lg font-heading text-lg font-bold",
          onDark ? "bg-sidebar-accent text-sidebar-fg" : "bg-secondary text-foreground")}>
          {shortName.charAt(0).toUpperCase()}
        </span>
      </span>
    );
  }

  // ---- Login: login logo → full → text lockup ----
  if (variant === "login") {
    const src = assets.login?.url || fullSrc;
    return (
      <span className={cn("flex items-center", className)} data-testid="nirvana-logo">
        {src ? <Img src={src} alt={shortName} />
          : <TextLockup shortName={shortName} productLabel={productLabel} onDark={onDark} scale="lg" />}
      </span>
    );
  }

  // ---- Mobile: prefer compact mark, then full, else responsive text lockup ----
  if (variant === "mobile") {
    const src = markSrc || fullSrc;
    return (
      <span className={cn("flex items-center", className)} data-testid="nirvana-logo">
        {src ? <Img src={src} alt={shortName} />
          : <TextLockup shortName={shortName} productLabel={productLabel} onDark={onDark} scale="sm" />}
      </span>
    );
  }

  // ---- Desktop / expanded header: Primary Full Logo, else two-line text lockup (never the mark) ----
  return (
    <span className={cn("flex items-center", className)} data-testid="nirvana-logo">
      {fullSrc ? <Img src={fullSrc} alt={shortName} />
        : <TextLockup shortName={shortName} productLabel={productLabel} onDark={onDark} scale="md" />}
    </span>
  );
}
